// libraries
import { createSelector } from 'reselect';

// state
import type { AvoState, StateTree } from '@makemydeal/dr-dash-types';

// selectors
import * as vehicleSelectors from './vehicleSelectors';
import * as offerReduxSelectors from './offerRedux';

const getAvoState = (state: StateTree): AvoState => state.aiVehicleRecommendationsStale ?? { isStale: true, lastFetchedAt: null };

// Wrapped (not passed by direct reference) so jest.spyOn on the namespace import is honored at call time.
const hasBaseVehicle = (state: StateTree): boolean => vehicleSelectors.getHasVehicle(state);
const hasCustomer = (state: StateTree): boolean => !offerReduxSelectors.getIsShopperInfoMissed(state);

export const getAvoIsStale = (state: StateTree): boolean => (state.aiVehicleRecommendationsStale ?? { isStale: true }).isStale;

/**
 * AC-7.4: generating recommendations requires a primary/starting vehicle AND a customer on the
 * deal. This vehicle+customer guard takes precedence over staleness — so even when AVO data is
 * stale/absent, the fetch is still suppressed if either is missing. For example, a Manager View
 * launched via Quick Quote without a vehicle has no base vehicle, so this returns false (no fetch).
 */
export const shouldFetchVehicleRecommendations = createSelector(
    [getAvoState, hasBaseVehicle, hasCustomer],
    (avoState, hasVehicle, hasCustomerPresent): boolean => {
        const isStaleOrAbsent = avoState.isStale === true || avoState.lastFetchedAt === null;
        return isStaleOrAbsent && hasVehicle && hasCustomerPresent;
    }
);
